/**
 * Best-effort /24 CIDR for the browser's LAN using WebRTC host candidates.
 * Works for many RFC1918 home/office networks when the dashboard is opened on that machine.
 */
function isRfc1918IPv4(ip) {
  if (!ip || typeof ip !== 'string') return false;
  const parts = ip.split('.');
  if (parts.length !== 4) return false;
  const [a, b] = parts.map((x) => parseInt(x, 10));
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  return false;
}

function toSlash24(ip) {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  return `${parts[0]}.${parts[1]}.${parts[2]}.0/24`;
}

/**
 * @param {number} [timeoutMs]
 * @returns {Promise<string|null>} e.g. "192.168.1.0/24"
 */
export function detectBrowserLanCidr(timeoutMs = 2800) {
  if (typeof RTCPeerConnection === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const candidates = new Set();
    let settled = false;
    /** @type {RTCPeerConnection | null} */
    let pc = null;
    let timer = 0;
    const done = (val) => {
      if (settled) return;
      settled = true;
      try {
        pc?.close();
      } catch {
        /* ignore */
      }
      clearTimeout(timer);
      resolve(val);
    };

    pc = new RTCPeerConnection({ iceServers: [] });
    timer = setTimeout(() => {
      for (const ip of candidates) {
        if (isRfc1918IPv4(ip)) {
          done(toSlash24(ip));
          return;
        }
      }
      const first = candidates.values().next().value;
      done(first ? toSlash24(first) : null);
    }, timeoutMs);

    try {
      pc.createDataChannel('');
      pc
        .createOffer()
        .then((o) => pc.setLocalDescription(o))
        .catch(() => done(null));
    } catch {
      done(null);
      return;
    }

    pc.onicecandidate = (e) => {
      if (e.candidate && e.candidate.candidate) {
        const m = e.candidate.candidate.match(/([0-9]{1,3}(?:\.[0-9]{1,3}){3})/);
        if (m) {
          const ip = m[1];
          if (ip && !ip.startsWith('127.')) {
            candidates.add(ip);
            if (isRfc1918IPv4(ip)) {
              const cidr = toSlash24(ip);
              if (cidr) done(cidr);
            }
          }
        }
      } else if (e.candidate == null) {
        for (const ip of candidates) {
          if (isRfc1918IPv4(ip)) {
            done(toSlash24(ip));
            return;
          }
        }
        const first = candidates.values().next().value;
        done(first ? toSlash24(first) : null);
      }
    };
  });
}
